import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from './api';
import './StatsPage.css';

function BracketPrediction() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const response = await api.post('/api/bracket-prediction', { url: url.trim() });
      setData(response.data);
    } catch (err) {
      let message = 'Request failed';
      if (err?.response) {
        const detail = err.response?.data?.detail || err.response?.data?.message;
        message = `HTTP ${err.response.status}${detail ? ': ' + detail : ''}`;
      } else if (err?.message) {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stats-wrap">
      <button type="button" className="back-btn" onClick={() => navigate(-1)} title="Go back">&#8592; Back</button>
      <h2 className="page-title">Bracket Prediction</h2>
      <form onSubmit={handleSubmit} className="stats-controls">
        <div className="controls-row">
          <label className="control" style={{ flex: '1 1 0' }}>
            <span className="label">Bracket URL</span>
            <input
              className="input"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://fbjjf.smoothcomp.com/en/event/29283/bracket/2103903"
            />
          </label>
          <button type="submit" className="btn">Predict</button>
        </div>
      </form>

      {loading && <div>Loading...</div>}
      {error && (
        <div style={{ color: 'salmon', whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      {data && (
        <div style={{ marginTop: 16 }}>
          {data.bracket_name && (
            <div style={{ fontSize: '1.1em', fontWeight: 500, marginBottom: 12 }}>{data.bracket_name}</div>
          )}

          {data.players && data.players.length > 0 && (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#1a1a22', borderRadius: 8 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>Players ({data.players.length})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                {data.players.slice().sort((a, b) => b.elo - a.elo).map((p, i) => (
                  <span key={i} style={{ fontSize: '0.85em' }}>
                    <Link className="competitor-name" to={`/${encodeURIComponent(p.name)}`} title={`Open ${p.name}`}>
                      {p.name}
                    </Link>
                    {' '}<span style={{ opacity: 0.6 }}>{p.elo}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {data.matches && data.matches.length > 0 && (() => {
            let lastRound = null;
            return (
              <div className="series-card">
                <div className="competitor-list">
                  {data.matches.map((m, i) => {
                    const left = m.left;
                    const right = m.right;
                    const winner = m.winner;
                    const isPredicted = m.state === 'predicted';
                    const leftWins = winner && left && winner.name === left.name;
                    const rightWins = winner && right && winner.name === right.name;
                    const lp = m.left_win_prob || 0;
                    const rp = m.right_win_prob || 0;
                    const barWidth = `${Math.round(lp * 100)}%`;
                    const showSeparator = lastRound !== null && lastRound !== m.round;
                    lastRound = m.round;

                    return (
                      <React.Fragment key={i}>
                        {showSeparator && (
                          <div style={{ textAlign: 'center', padding: '12px 0 8px', fontSize: '0.7em', opacity: 0.4, letterSpacing: '0.1em', textTransform: 'uppercase', borderTop: '1px solid #2a2a35' }}>
                            {m.name || `Round ${m.round}`}
                          </div>
                        )}
                        <div className="competitor-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4, padding: '12px 14px' }}>
                          {!showSeparator && (
                            <div style={{ fontSize: '0.7em', opacity: 0.4 }}>
                              {m.name || `Round ${m.round} - Match ${m.match_nr}`}
                            </div>
                          )}

                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: leftWins ? 700 : 400, color: leftWins ? '#22c55e' : 'inherit' }}>
                              {left ? left.name : 'TBD'}
                            </span>
                            {left && left.club && <span style={{ opacity: 0.4, fontSize: '0.8em' }}>{left.club}</span>}
                            <span style={{ marginLeft: 'auto', fontSize: '0.85em', opacity: 0.6 }}>{left ? (m.left_elo || '--') : ''}</span>
                            <span style={{ minWidth: 48, textAlign: 'right', fontWeight: 600, color: leftWins ? '#22c55e' : 'inherit' }}>
                              {isPredicted && left ? `${Math.round(lp * 100)}%` : ''}
                            </span>
                          </div>
                          {left && (left.gold_prob > 0 || left.bronze_prob > 0) && (
                            <div style={{ display: 'flex', gap: 8, fontSize: '0.7em', opacity: 0.6, paddingLeft: 4 }}>
                              {left.gold_prob > 0 && <span style={{ color: '#eab308' }}>🥇 {Math.round(left.gold_prob * 100)}%</span>}
                              {left.bronze_prob > 0 && <span style={{ color: '#cd7f32' }}>🥉 {Math.round(left.bronze_prob * 100)}%</span>}
                            </div>
                          )}

                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: rightWins ? 700 : 400, color: rightWins ? '#22c55e' : 'inherit' }}>
                              {right ? right.name : 'TBD'}
                            </span>
                            {right && right.club && <span style={{ opacity: 0.4, fontSize: '0.8em' }}>{right.club}</span>}
                            <span style={{ marginLeft: 'auto', fontSize: '0.85em', opacity: 0.6 }}>{right ? (m.right_elo || '--') : ''}</span>
                            <span style={{ minWidth: 48, textAlign: 'right', fontWeight: 600, color: rightWins ? '#22c55e' : 'inherit' }}>
                              {isPredicted && right ? `${Math.round(rp * 100)}%` : ''}
                            </span>
                          </div>
                          {right && (right.gold_prob > 0 || right.bronze_prob > 0) && (
                            <div style={{ display: 'flex', gap: 8, fontSize: '0.7em', opacity: 0.6, paddingLeft: 4 }}>
                              {right.gold_prob > 0 && <span style={{ color: '#eab308' }}>🥇 {Math.round(right.gold_prob * 100)}%</span>}
                              {right.bronze_prob > 0 && <span style={{ color: '#cd7f32' }}>🥉 {Math.round(right.bronze_prob * 100)}%</span>}
                            </div>
                          )}

                          {isPredicted && left && right && (
                            <div style={{ height: 4, borderRadius: 2, background: '#2a2a35', overflow: 'hidden', marginTop: 2 }}>
                              <div style={{ width: barWidth, height: '100%', background: '#6366f1', borderRadius: 2 }} />
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {data.matches && data.matches.length > 0 && (() => {
            const sorted = data.matches.slice().sort((a, b) => b.round - a.round);
            const isBronze = (m) => {
              const n = (m.name || '').toLowerCase();
              return n.includes('bronze') || n.includes('third');
            };
            // The bronze match can share the final round, so the last match in
            // sorted order is NOT necessarily the final. Prefer the match
            // actually named "Final", then the last non-bronze match.
            const finalMatch =
              sorted.find((m) => !isBronze(m) && /(^|\s)final$/i.test((m.name || '').trim())) ||
              sorted.find((m) => !isBronze(m));
            const goldWinner = finalMatch?.winner;
            const silverWinner = finalMatch?.loser;
            if (!goldWinner) return null;
            const bronzeMatch = sorted.find(isBronze);
            const bronzeWinner = bronzeMatch?.winner;
            return (
              <div style={{ marginTop: 16, padding: '16px', background: '#1a1a22', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: '0.85em', opacity: 0.6, marginBottom: 4 }}>Predicted Winner</div>
                <div style={{ fontSize: '1.3em', fontWeight: 700, color: '#22c55e' }}>
                  {goldWinner.name}
                </div>
                {goldWinner.club && <div style={{ opacity: 0.6, fontSize: '0.85em' }}>{goldWinner.club}</div>}
                {goldWinner.gold_prob > 0 && (
                  <div style={{ fontSize: '0.8em', color: '#eab308', marginTop: 2 }}>
                    {Math.round(goldWinner.gold_prob * 100)}% probability
                  </div>
                )}
                {silverWinner && (
                  <>
                    <div style={{ fontSize: '0.85em', opacity: 0.6, marginTop: 12, marginBottom: 4 }}>Predicted Silver</div>
                    <div style={{ fontSize: '1.2em', fontWeight: 700, color: '#94a3b8' }}>
                      {silverWinner.name}
                    </div>
                    {silverWinner.club && <div style={{ opacity: 0.6, fontSize: '0.85em' }}>{silverWinner.club}</div>}
                    {silverWinner.silver_prob > 0 && (
                      <div style={{ fontSize: '0.8em', color: '#94a3b8', marginTop: 2 }}>
                        {Math.round(silverWinner.silver_prob * 100)}% probability
                      </div>
                    )}
                  </>
                )}
                {bronzeWinner && (
                  <>
                    <div style={{ fontSize: '0.85em', opacity: 0.6, marginTop: 12, marginBottom: 4 }}>Predicted Bronze</div>
                    <div style={{ fontSize: '1.1em', fontWeight: 700, color: '#cd7f32' }}>
                      {bronzeWinner.name}
                    </div>
                    {bronzeWinner.club && <div style={{ opacity: 0.6, fontSize: '0.85em' }}>{bronzeWinner.club}</div>}
                    {bronzeWinner.bronze_prob > 0 && (
                      <div style={{ fontSize: '0.8em', color: '#cd7f32', marginTop: 2 }}>
                        {Math.round(bronzeWinner.bronze_prob * 100)}% probability
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {data.players && data.players.length > 0 && (() => {
            const sorted = data.players.slice().sort((a, b) => b.gold_prob - a.gold_prob);
            const withMedals = sorted.filter(p => p.gold_prob > 0.001 || p.silver_prob > 0.001 || p.bronze_prob > 0.001);
            if (withMedals.length === 0) return null;
            return (
              <div style={{ marginTop: 16, padding: '12px 16px', background: '#1a1a22', borderRadius: 8 }}>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>Medal Probabilities</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {withMedals.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85em' }}>
                      <Link className="competitor-name" to={`/${encodeURIComponent(p.name)}`} title={`Open ${p.name}`}>
                        {p.name}
                      </Link>
                      <span style={{ opacity: 0.4, fontSize: '0.85em' }}>{p.elo}</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
                        <span style={{ color: '#eab308', minWidth: 48, textAlign: 'right' }}>
                          🥇 {Math.round(p.gold_prob * 100)}%
                        </span>
                        <span style={{ color: '#94a3b8', minWidth: 48, textAlign: 'right' }}>
                          🥈 {Math.round((p.silver_prob || 0) * 100)}%
                        </span>
                        <span style={{ color: '#cd7f32', minWidth: 48, textAlign: 'right' }}>
                          🥉 {Math.round(p.bronze_prob * 100)}%
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {data.players && data.players.length > 0 && (() => {
            const sorted = data.players.slice().sort((a, b) => b.elo - a.elo);
            return (
              <div style={{ marginTop: 16, padding: '12px 16px', background: '#1a1a22', borderRadius: 8 }}>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>
                  ELO Comparison (Current / Adjusted)
                  {data.bracket_weight_kg && (
                    <span style={{ opacity: 0.5, fontWeight: 400, marginLeft: 8, fontSize: '0.85em' }}>
                      Bracket: {data.bracket_weight_kg}kg
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {sorted.map((p, i) => {
                    const diff = (p.adjusted_elo || p.elo) - p.elo;
                    const diffStr = diff > 0 ? `+${Math.round(diff)}` : diff < 0 ? `${Math.round(diff)}` : '0';
                    const diffColor = diff > 0 ? '#22c55e' : diff < 0 ? '#ef4444' : '#666';
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85em' }}>
                        <Link className="competitor-name" to={`/${encodeURIComponent(p.name)}`} title={`Open ${p.name}`}>
                          {p.name}
                        </Link>
                        <span style={{ opacity: 0.5, fontSize: '0.8em', minWidth: 45 }}>
                          {p.avg_weight_kg ? `${p.avg_weight_kg}kg` : ''}
                        </span>
                        <span style={{ opacity: 0.5, fontSize: '0.8em', minWidth: 45 }}>
                          {p.weight_diff_kg != null ? (
                            <span style={{ color: p.weight_diff_kg > 0 ? '#eab308' : p.weight_diff_kg < 0 ? '#6366f1' : '#666' }}>
                              {p.weight_diff_kg > 0 ? '+' : ''}{p.weight_diff_kg}kg
                            </span>
                          ) : ''}
                        </span>
                        <span style={{ opacity: 0.5, fontSize: '0.8em', minWidth: 40 }}>
                          {p.years_exp > 0 ? `${p.years_exp}y` : ''}
                        </span>
                        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ opacity: 0.6, minWidth: 40, textAlign: 'right' }}>{Math.round(p.elo)}</span>
                          <span style={{ opacity: 0.3 }}>&rarr;</span>
                          <span style={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{Math.round(p.adjusted_elo || p.elo)}</span>
                          <span style={{ color: diffColor, fontSize: '0.8em', minWidth: 30, textAlign: 'right' }}>{diffStr}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 8, fontSize: '0.7em', opacity: 0.4 }}>
                  Weight: recent 3-month average. Diff = recent avg vs bracket weight (heavier = had to cut = stronger). Experience: years competing.
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default BracketPrediction;
